<template>
  <Teleport to="body">
    <Transition name="overlay-fade">
      <div
        v-if="isOpen && !isComplete"
        class="start-round-overlay"
        :class="{ shaking: animationPhase === 'ignition' }"
        role="dialog"
        aria-modal="true"
        aria-label="Start Round"
      >
        <!-- Background particles container -->
        <div class="particles-container" ref="particlesRef"></div>

        <!-- Rocket Button Container -->
        <div
          class="rocket-container"
          :class="[animationPhase, { launched: animationPhase === 'blastoff' || animationPhase === 'complete' }]"
        >
          <!-- Rocket Engine (appears on click) -->
          <div class="rocket-engine" :class="{ visible: showEngine }">
            <div class="engine-body"></div>
            <div class="flames">
              <div class="flame flame-1"></div>
              <div class="flame flame-2"></div>
              <div class="flame flame-3"></div>
            </div>
          </div>

          <!-- Main Rocket Button -->
          <button
            class="rocket-button"
            data-tutorial="start-round-btn"
            :class="{ pulsing: animationPhase === 'idle' }"
            :disabled="animationPhase !== 'idle'"
            @click="handleLaunch"
          >
            <span class="rocket-emoji left">🚀</span>
            <span class="button-text">START ROUND {{ roundNumber }}!</span>
            <span class="rocket-emoji right">🚀</span>
          </button>
        </div>

        <!-- Starburst effect (shows after blast off) -->
        <div
          v-if="showStarburst"
          class="starburst"
          :style="starburstPosition"
        >
          <span class="burst-particle" v-for="i in 12" :key="i">✨</span>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

// Props
const props = defineProps<{
  isOpen: boolean
  roundNumber: number
}>()

// Emits
const emit = defineEmits<{
  (e: 'launch'): void
  (e: 'complete'): void
}>()

// Animation state
type AnimationPhase = 'idle' | 'engine' | 'ignition' | 'blastoff' | 'complete'
const animationPhase = ref<AnimationPhase>('idle')
const showEngine = ref(false)
const showStarburst = ref(false)
const isComplete = ref(false)
const starburstPosition = ref({ left: '50%', top: '50%' })
const particlesRef = ref<HTMLElement | null>(null)

// Check for reduced motion preference
const prefersReducedMotion = typeof window !== 'undefined'
  ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
  : false

// Debug logging
console.log('[StartRoundOverlay] Component mounted, isOpen:', props.isOpen, 'roundNumber:', props.roundNumber)
watch(() => props.isOpen, (newVal) => {
  console.log('[StartRoundOverlay] isOpen changed to:', newVal, 'roundNumber:', props.roundNumber)
})

// Animation timing constants
const PHASES = {
  ENGINE_POP: 250,
  IGNITION: 400,
  BLAST_OFF: 600,
  STARBURST: 400,
}

// Utility sleep function
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Play sound effect (if SoundManager exists)
function playSound(sound: string): void {
  try {
    const soundManager = (window as any).SoundManager
    if (soundManager?.play) {
      soundManager.play(sound)
    }
  } catch {
    // Sound not available, that's okay
  }
}

// Spawn trail particles during blast off
function spawnTrailParticles(): void {
  const container = particlesRef.value
  if (!container) return

  const emojis = ['⭐', '✨', '💫', '🔥', '💥', '🌟']

  for (let i = 0; i < 25; i++) {
    const particle = document.createElement('div')
    particle.className = 'trail-particle'
    particle.textContent = emojis[Math.floor(Math.random() * emojis.length)] ?? '⭐'

    // Start from center-left (where rocket was)
    const startX = 30 + Math.random() * 20
    const startY = 45 + (Math.random() - 0.5) * 20

    particle.style.cssText = `
      position: absolute;
      left: ${startX}%;
      top: ${startY}%;
      font-size: ${20 + Math.random() * 20}px;
      opacity: 1;
      transform: scale(1);
      animation: particle-fade ${800 + Math.random() * 400}ms ease-out forwards;
      animation-delay: ${i * 40}ms;
      pointer-events: none;
      z-index: 10;
    `

    container.appendChild(particle)

    // Clean up after animation
    setTimeout(() => {
      particle.remove()
    }, 1500)
  }
}

// Spawn ignition flame particles
function spawnFlameParticles(): void {
  const container = particlesRef.value
  if (!container) return

  const flameEmojis = ['🔥', '💥', '⚡', '✨']

  for (let i = 0; i < 15; i++) {
    const particle = document.createElement('div')
    particle.className = 'flame-particle'
    particle.textContent = flameEmojis[Math.floor(Math.random() * flameEmojis.length)] ?? '🔥'

    // Spawn around the engine area
    const angle = (Math.random() - 0.5) * 120 // Spread in a cone to the left
    const distance = 50 + Math.random() * 100
    const rad = (angle - 180) * (Math.PI / 180)

    const startX = 35 // Left of button
    const startY = 50
    const endX = startX + Math.cos(rad) * distance / 10
    const endY = startY + Math.sin(rad) * distance / 10

    particle.style.cssText = `
      position: absolute;
      left: ${startX}%;
      top: ${startY}%;
      font-size: ${24 + Math.random() * 16}px;
      opacity: 1;
      transform: translate(-50%, -50%) scale(0.5);
      transition: all ${300 + Math.random() * 200}ms ease-out;
      pointer-events: none;
      z-index: 10;
    `

    container.appendChild(particle)

    // Animate outward
    requestAnimationFrame(() => {
      particle.style.left = `${endX}%`
      particle.style.top = `${endY}%`
      particle.style.opacity = '0'
      particle.style.transform = 'translate(-50%, -50%) scale(1.5)'
    })

    // Clean up
    setTimeout(() => {
      particle.remove()
    }, 600)
  }
}

// Main launch handler
async function handleLaunch(): Promise<void> {
  if (animationPhase.value !== 'idle') return

  // Reduced motion: skip animations
  if (prefersReducedMotion) {
    animationPhase.value = 'complete'
    emit('launch')
    await sleep(100)
    isComplete.value = true
    emit('complete')
    return
  }

  // Phase 1: Engine pops out with "boing" effect
  animationPhase.value = 'engine'
  showEngine.value = true
  playSound('click')
  await sleep(PHASES.ENGINE_POP)

  // Phase 2: Ignition with screen shake
  animationPhase.value = 'ignition'
  playSound('card_play') // Use existing sound as ignition rumble
  spawnFlameParticles()
  await sleep(PHASES.IGNITION)

  // Phase 3: BLAST OFF!
  animationPhase.value = 'blastoff'
  playSound('card_draw') // Whoosh sound
  // Clean rocket - no trail particles

  // Emit launch event immediately (don't wait for animation)
  emit('launch')

  await sleep(PHASES.BLAST_OFF)

  // Phase 4: Complete (no starburst for clean look)
  animationPhase.value = 'complete'
  await sleep(PHASES.STARBURST)

  // Complete!
  isComplete.value = true
  emit('complete')
}

// Expose method to trigger launch from parent (for multiplayer sync)
function triggerLaunch(): void {
  if (animationPhase.value === 'idle') {
    handleLaunch()
  }
}

// Reset when overlay closes
watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    // Reset state when opening
    animationPhase.value = 'idle'
    showEngine.value = false
    showStarburst.value = false
    isComplete.value = false
  }
})

// Expose methods to parent
defineExpose({
  triggerLaunch
})
</script>

<style scoped>
/* Overlay */
.start-round-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  overflow: hidden;
}

/* Screen shake during ignition */
.start-round-overlay.shaking {
  animation: shake 0.4s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0) rotate(0); }
  10% { transform: translateX(-8px) rotate(-0.5deg); }
  20% { transform: translateX(8px) rotate(0.5deg); }
  30% { transform: translateX(-6px) rotate(-0.3deg); }
  40% { transform: translateX(6px) rotate(0.3deg); }
  50% { transform: translateX(-4px) rotate(-0.2deg); }
  60% { transform: translateX(4px) rotate(0.2deg); }
  70% { transform: translateX(-2px) rotate(-0.1deg); }
  80% { transform: translateX(2px) rotate(0.1deg); }
  90% { transform: translateX(-1px); }
}

/* Particles container */
.particles-container {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

/* Rocket Container */
.rocket-container {
  position: relative;
  display: flex;
  align-items: center;
  transition: transform 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

/* Blast off animation */
.rocket-container.launched {
  animation: blast-off 0.6s cubic-bezier(0.5, 0, 1, 1) forwards;
}

@keyframes blast-off {
  0% {
    transform: translateX(0) rotate(0);
  }
  15% {
    transform: translateX(-30px) rotate(-8deg);
  }
  100% {
    transform: translateX(150vw) rotate(15deg);
  }
}

/* Rocket Engine */
.rocket-engine {
  position: absolute;
  right: 100%;
  display: flex;
  align-items: center;
  opacity: 0;
  transform: translateX(50px) scale(0);
  transition: all 0.25s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.rocket-engine.visible {
  opacity: 1;
  transform: translateX(0) scale(1);
}

.engine-body {
  width: 30px;
  height: 40px;
  background: linear-gradient(135deg, #666 0%, #444 100%);
  border-radius: 8px 0 0 8px;
  border: 3px solid #333;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.5);
}

/* Flames */
.flames {
  position: absolute;
  right: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.flame {
  border-radius: 50% 50% 50% 50%;
  animation: flame-flicker 0.1s ease-in-out infinite alternate;
}

.flame-1 {
  width: 40px;
  height: 20px;
  background: linear-gradient(90deg, transparent 0%, #ff4500 30%, #ffd700 60%, #fff 100%);
  animation-delay: 0ms;
}

.flame-2 {
  width: 60px;
  height: 25px;
  background: linear-gradient(90deg, transparent 0%, #ff6f00 20%, #ffd700 50%, #fff 100%);
  animation-delay: 33ms;
}

.flame-3 {
  width: 40px;
  height: 20px;
  background: linear-gradient(90deg, transparent 0%, #ff4500 30%, #ffd700 60%, #fff 100%);
  animation-delay: 66ms;
}

@keyframes flame-flicker {
  0% {
    transform: scaleX(0.9) scaleY(0.95);
    opacity: 0.9;
  }
  100% {
    transform: scaleX(1.1) scaleY(1.05);
    opacity: 1;
  }
}

/* During ignition, flames get bigger */
.rocket-container.ignition .flame-1 {
  width: 80px;
  height: 35px;
}

.rocket-container.ignition .flame-2 {
  width: 120px;
  height: 45px;
}

.rocket-container.ignition .flame-3 {
  width: 80px;
  height: 35px;
}

/* Rocket Button */
.rocket-button {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 48px;
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  background: linear-gradient(135deg, #ffd700 0%, #ff8c00 50%, #ff6f00 100%);
  border: 4px solid #fff;
  border-radius: 20px;
  cursor: pointer;
  box-shadow:
    0 8px 32px rgba(255, 140, 0, 0.5),
    0 4px 16px rgba(0, 0, 0, 0.3),
    inset 0 2px 0 rgba(255, 255, 255, 0.3);
  transition: all 0.15s ease;
  position: relative;
  overflow: hidden;
}

/* Shiny sweep effect */
.rocket-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 100%
  );
  animation: sweep 2s ease-in-out infinite;
}

@keyframes sweep {
  0% { left: -100%; }
  50% { left: 100%; }
  100% { left: 100%; }
}

/* Idle pulse animation */
.rocket-button.pulsing {
  animation: rocket-float 2s ease-in-out infinite;
}

@keyframes rocket-float {
  0%, 100% {
    transform: translateY(0) scale(1);
    box-shadow:
      0 8px 32px rgba(255, 140, 0, 0.5),
      0 4px 16px rgba(0, 0, 0, 0.3),
      inset 0 2px 0 rgba(255, 255, 255, 0.3);
  }
  50% {
    transform: translateY(-8px) scale(1.02);
    box-shadow:
      0 16px 48px rgba(255, 140, 0, 0.6),
      0 8px 24px rgba(0, 0, 0, 0.3),
      inset 0 2px 0 rgba(255, 255, 255, 0.3);
  }
}

.rocket-button:hover:not(:disabled) {
  transform: translateY(-4px) scale(1.05);
  box-shadow:
    0 12px 40px rgba(255, 140, 0, 0.6),
    0 6px 20px rgba(0, 0, 0, 0.3),
    inset 0 2px 0 rgba(255, 255, 255, 0.3);
}

.rocket-button:active:not(:disabled) {
  transform: translateY(0) scale(0.98);
}

.rocket-button:disabled {
  cursor: default;
}

/* Rocket emojis */
.rocket-emoji {
  font-size: 36px;
  display: inline-block;
}

.rocket-emoji.left {
  animation: wiggle-left 1s ease-in-out infinite;
}

.rocket-emoji.right {
  animation: wiggle-right 1s ease-in-out infinite;
}

@keyframes wiggle-left {
  0%, 100% { transform: rotate(-10deg); }
  50% { transform: rotate(10deg); }
}

@keyframes wiggle-right {
  0%, 100% { transform: rotate(10deg); }
  50% { transform: rotate(-10deg); }
}

.button-text {
  letter-spacing: 2px;
}

/* Starburst effect */
.starburst {
  position: absolute;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 20;
}

.burst-particle {
  position: absolute;
  font-size: 32px;
  animation: burst 0.4s ease-out forwards;
}

.burst-particle:nth-child(1) { --angle: 0deg; }
.burst-particle:nth-child(2) { --angle: 30deg; }
.burst-particle:nth-child(3) { --angle: 60deg; }
.burst-particle:nth-child(4) { --angle: 90deg; }
.burst-particle:nth-child(5) { --angle: 120deg; }
.burst-particle:nth-child(6) { --angle: 150deg; }
.burst-particle:nth-child(7) { --angle: 180deg; }
.burst-particle:nth-child(8) { --angle: 210deg; }
.burst-particle:nth-child(9) { --angle: 240deg; }
.burst-particle:nth-child(10) { --angle: 270deg; }
.burst-particle:nth-child(11) { --angle: 300deg; }
.burst-particle:nth-child(12) { --angle: 330deg; }

@keyframes burst {
  0% {
    transform: rotate(var(--angle)) translateX(0) scale(0.5);
    opacity: 1;
  }
  100% {
    transform: rotate(var(--angle)) translateX(150px) scale(1.2);
    opacity: 0;
  }
}

/* Particle fade animation */
@keyframes particle-fade {
  0% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.3) translateY(20px);
  }
}

/* Overlay transitions */
.overlay-fade-enter-active {
  transition: opacity 0.3s ease;
}

.overlay-fade-leave-active {
  transition: opacity 0.2s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .rocket-button {
    padding: 20px 32px;
    font-size: 22px;
    gap: 12px;
  }

  .rocket-emoji {
    font-size: 28px;
  }

  .engine-body {
    width: 24px;
    height: 32px;
  }

  .flame-1, .flame-3 {
    width: 30px;
    height: 16px;
  }

  .flame-2 {
    width: 45px;
    height: 20px;
  }
}

@media (max-width: 480px) {
  .rocket-button {
    padding: 16px 24px;
    font-size: 18px;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .rocket-emoji {
    font-size: 24px;
  }

  .button-text {
    flex-basis: 100%;
    text-align: center;
  }
}
</style>
